import {
  FilterDocument,
  FilterDocumentDateFilterTypes,
  FilterDocumentFilters,
  FilterDocumentTagMatchMode,
  Goal,
  GoalImpactLevel,
  goalOtherID,
  ID,
  IDPrefixes,
  LifeArea,
  lifeAreaOtherID,
  Relationship,
  Tag,
  TaskFilterOptions,
  Timestamp,
  User,
} from '@griply/types';
import { endOfDay, startOfDay } from 'date-fns';
import mergeWith from 'lodash/mergeWith';

import { buildFilterSlices, FilterSlices } from './build-filter-slices';
import {
  calculateDeadlinePresetDates,
  DeadlineDateRange,
} from './calculate-deadline-preset-dates';
import { getCommonUIDs } from './get-common-uids';
import { getDurationFilterOptions } from './get-duration-filter-options';
import { getLifeAreaGoalIds } from './get-life-area-goal-ids';
import { getWeekStartsOn } from './get-week-starts-on';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from './prefix-id';
import { resolveTagMatchMode } from './resolve-tag-match-mode';

const nonExistingId = 'non-existing-id';

export type TransformFilterDocumentOptions = {
  filterDocument: FilterDocument;
  goals: Goal[];
  tags: Tag[];
  relationships: Relationship[];
  lifeAreas: LifeArea[];
  user: User;
  today: Timestamp;
  uncompletedOnly?: boolean;
  includeTaskWithAction?: boolean;
  excludeHiddenTodoHabits?: boolean;
  includeArchivedGoals?: boolean;
  includeArchived?: boolean;
  slices?: FilterSlices;
};

export const transformFilterDocument = (
  options: TransformFilterDocumentOptions,
): TaskFilterOptions[] => {
  const {
    filterDocument,
    goals,
    tags,
    relationships,
    lifeAreas,
    user,
    today,
    uncompletedOnly,
    includeTaskWithAction,
    excludeHiddenTodoHabits,
    includeArchivedGoals = false,
    includeArchived = false,
    slices,
  } = options;

  const resolvedSlices =
    slices ?? buildFilterSlices(goals, tags, relationships, lifeAreas);
  const {
    tagIdsSet,
    lifeAreaIdsSet,
    goalIdsSet,
    relationshipsMap,
    lifeAreaGoalIdsMap,
  } = resolvedSlices;
  const weekStartsOn = getWeekStartsOn(user);

  const archivedGoalIds = includeArchivedGoals
    ? undefined
    : resolvedSlices.archivedGoalIds;

  // Helper to calculate a single set of filter options
  const calculateOptions = (
    fdFilters: Partial<FilterDocumentFilters>,
  ): TaskFilterOptions => {
    // life area filter
    const lifeAreaFilter = fdFilters?.lifeArea;
    const filteredLifeAreaIds = lifeAreaFilter?.include
      ?.filter((id: ID) => id !== lifeAreaOtherID && lifeAreaIdsSet.has(id))
      ?.sort();

    const lifeAreaIds = lifeAreaFilter?.include?.length
      ? filteredLifeAreaIds?.length
        ? filteredLifeAreaIds
        : undefined
      : lifeAreaFilter?.include;

    const noLifeArea = lifeAreaFilter?.include?.includes(lifeAreaOtherID);

    // "No life area" must also exclude tasks that inherit a life area from their
    // goal, so collect every goal whose chain resolves to one.
    const lifeAreaLinkedGoalIds = noLifeArea
      ? resolvedSlices.lifeAreaLinkedGoalIds
      : undefined;

    // get all goals from the selected lifeArea's
    const lifeAreaGoalIds = lifeAreaIds?.length
      ? getLifeAreaGoalIds(lifeAreaIds, lifeAreaGoalIdsMap).sort()
      : undefined;

    // active goal filter
    const activeGoalFilter = fdFilters?.goalActive;
    const activeGoalTimeframe =
      activeGoalFilter?.type === FilterDocumentDateFilterTypes.Custom
        ? {
            start: activeGoalFilter?.custom?.start
              ? startOfDay(activeGoalFilter?.custom?.start)
              : undefined,
            end: activeGoalFilter?.custom?.end
              ? startOfDay(activeGoalFilter?.custom?.end)
              : undefined,
          }
        : activeGoalFilter
          ? calculateDeadlinePresetDates(activeGoalFilter.type, {
              today,
              weekStartsOn,
            }) ?? { start: undefined, end: undefined }
          : { start: undefined, end: undefined };
    const activeGoalStart =
      (activeGoalTimeframe as DeadlineDateRange).strictStart ??
      activeGoalTimeframe.start;
    const activeGoalEnd = activeGoalTimeframe.end;

    const activeGoalIds =
      activeGoalStart && activeGoalEnd
        ? goals
            .filter(
              (goal) =>
                goal.startDate &&
                goal.deadline &&
                goal.startDate <= activeGoalEnd &&
                goal.deadline >= activeGoalStart,
            )
            .map((goal) => goal.id)
        : undefined;

    // goal filter
    const goalFilter = fdFilters?.goal;
    // filtering non-existing goals from the include list
    const filteredGoalIds = goalFilter?.include
      ?.filter((id: ID) => id !== goalOtherID && goalIdsSet.has(id))
      ?.slice()
      .sort();
    const goalIds = goalFilter?.include?.length
      ? filteredGoalIds?.length
        ? filteredGoalIds
        : undefined
      : undefined;

    const noGoal = goalFilter?.include?.includes(goalOtherID) ?? false;

    // goal impact filter
    const goalImpactFilter = fdFilters?.goalImpact;
    const goalImpactValues = goalImpactFilter?.include;
    const impactGoalIds = goalImpactValues?.length
      ? goals
          .filter((goal) => {
            if (goal.impact == null) {
              return goalImpactValues.includes('none' as GoalImpactLevel);
            }
            return goalImpactValues.includes(goal.impact);
          })
          .map((goal) => goal.id)
      : undefined;

    const finalGoalIds = getCommonUIDs(
      goalIds,
      lifeAreaGoalIds,
      activeGoalIds,
      impactGoalIds,
    );

    // Tag filter — branches on matchMode (Any = union, All = intersection, NoTag = tasks without any tag).
    const tagFilter = fdFilters?.tag;
    const filteredTagIds = tagFilter?.include?.filter((tagId) =>
      tagIdsSet.has(tagId),
    );
    const tagIds = tagFilter?.include?.length
      ? filteredTagIds?.length
        ? filteredTagIds
        : null
      : tagFilter?.include;

    const effectiveMatchMode = resolveTagMatchMode(tagFilter);
    const isNoTagMode = effectiveMatchMode === FilterDocumentTagMatchMode.NoTag;

    // For Any/All modes: compute union or intersection of tasks across selected tags.
    const taskIdsFromTagsSet =
      !isNoTagMode && tagIds?.length
        ? tagIds.reduce<Set<ID> | undefined>((acc, tagId) => {
            const currentTagTaskIds = new Set<ID>();
            relationshipsMap[createPrefixId(tagId, IDPrefixes.Tag)]?.forEach(
              (prefixedId) => {
                if (getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task) {
                  currentTagTaskIds.add(getPrefixedId(prefixedId));
                }
              },
            );
            if (!acc) {
              return currentTagTaskIds;
            }
            if (effectiveMatchMode === FilterDocumentTagMatchMode.All) {
              return new Set(
                [...acc].filter((id) => currentTagTaskIds.has(id)),
              );
            }
            return new Set([...acc, ...currentTagTaskIds]);
          }, undefined)
        : undefined;

    const taskIdsFromTags = taskIdsFromTagsSet
      ? Array.from(taskIdsFromTagsSet)
      : undefined;

    // NoTag mode: exclude every task that has any tag.
    const excludedTaskIds = isNoTagMode
      ? Array.from(
          Object.keys(relationshipsMap).reduce<Set<ID>>((acc, key) => {
            if (getPrefixedIdPrefix(key) === IDPrefixes.Tag) {
              relationshipsMap[key]?.forEach((prefixedId) => {
                if (getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task) {
                  acc.add(getPrefixedId(prefixedId));
                }
              });
            }
            return acc;
          }, new Set<ID>()),
        )
      : undefined;

    // Extract duration filter and convert minutes to milliseconds
    const { minDuration, maxDuration, noDuration, hasDuration } =
      getDurationFilterOptions(fdFilters?.duration);

    // Extract deleted filter
    const deleted = fdFilters?.deleted ?? false;

    // Extract deadline filter and convert to backend parameters
    const deadlineDeadlineFilter = fdFilters?.deadline;
    const isCustomDeadlineDeadline =
      deadlineDeadlineFilter?.type === FilterDocumentDateFilterTypes.Custom;
    const presetDeadlineDeadlineDates =
      deadlineDeadlineFilter && !isCustomDeadlineDeadline
        ? calculateDeadlinePresetDates(deadlineDeadlineFilter.type, {
            today,
            weekStartsOn,
          })
        : undefined;
    const deadlineDeadlineStartDate =
      deadlineDeadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      deadlineDeadlineFilter.custom?.start
        ? startOfDay(deadlineDeadlineFilter.custom.start)
        : presetDeadlineDeadlineDates?.start;
    const deadlineDeadlineEndDate =
      deadlineDeadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      deadlineDeadlineFilter.custom?.end
        ? endOfDay(deadlineDeadlineFilter.custom.end)
        : presetDeadlineDeadlineDates?.end;
    const deadlineDeadlineCompletedStartDate =
      deadlineDeadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      deadlineDeadlineFilter.custom?.start
        ? startOfDay(deadlineDeadlineFilter.custom.start)
        : presetDeadlineDeadlineDates?.strictStart ??
          presetDeadlineDeadlineDates?.start;
    const deadlineDeadlineCompletedEndDate = deadlineDeadlineEndDate;

    // Extract date filter (scheduled date) and convert to backend parameters
    const deadlineFilter = fdFilters?.date;
    const isCustomDeadline =
      deadlineFilter?.type === FilterDocumentDateFilterTypes.Custom;
    const presetDeadlineDates =
      deadlineFilter && !isCustomDeadline
        ? calculateDeadlinePresetDates(deadlineFilter.type, {
            today,
            weekStartsOn,
          })
        : undefined;
    const deadlineStartDate =
      deadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      deadlineFilter.custom?.start
        ? startOfDay(deadlineFilter.custom.start)
        : presetDeadlineDates?.start;
    const deadlineEndDate =
      deadlineFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      deadlineFilter.custom?.end
        ? endOfDay(deadlineFilter.custom.end)
        : presetDeadlineDates?.end;

    // Extract completedAt filter and convert to backend parameters
    const completedAtFilter = fdFilters?.completedAt;
    const isCustomCompletedAt =
      completedAtFilter?.type === FilterDocumentDateFilterTypes.Custom;
    const presetCompletedAtDates =
      completedAtFilter && !isCustomCompletedAt
        ? calculateDeadlinePresetDates(completedAtFilter.type, {
            today,
            weekStartsOn,
          })
        : undefined;
    const completedStartDate =
      completedAtFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      completedAtFilter.custom?.start
        ? startOfDay(completedAtFilter.custom.start)
        : presetCompletedAtDates?.strictStart ?? presetCompletedAtDates?.start;
    const completedEndDate =
      completedAtFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      completedAtFilter.custom?.end
        ? endOfDay(completedAtFilter.custom.end)
        : presetCompletedAtDates?.end;

    return {
      // if the array is empty, this means that the filter shouldn't match any task
      // if we would return undefined, it would mean that we don't filter on lifeArea
      // so we return a dummy id to make sure that no task is matched
      lifeAreas: lifeAreaIds?.length
        ? // when specifically filtering on goals, the life-area tasks are not allowed anymore
          goalIds?.length
          ? [nonExistingId]
          : lifeAreaIds
        : lifeAreaIds && !noLifeArea
          ? [nonExistingId]
          : undefined,
      // if the array is empty, this means that the filter shouldn't match any task
      // if we would return undefined, it would mean that we don't filter on goal
      // so we return a dummy id to make sure that no task is matched
      goals: finalGoalIds?.length
        ? finalGoalIds
        : finalGoalIds && !noGoal
          ? [nonExistingId]
          : undefined,
      types: fdFilters?.type?.include?.slice().sort() ?? undefined,
      priorities: fdFilters?.priority?.include?.slice().sort() ?? undefined,
      ids: taskIdsFromTags,
      excludeIds: excludedTaskIds,
      minDuration,
      maxDuration,
      noDuration,
      hasDuration,
      noDeadlineDeadline:
        deadlineDeadlineFilter?.type ===
        FilterDocumentDateFilterTypes.NoDeadline,
      hasDeadlineDeadline:
        deadlineDeadlineFilter?.type ===
        FilterDocumentDateFilterTypes.HasDeadline,
      noDeadline:
        deadlineFilter?.type === FilterDocumentDateFilterTypes.NoDeadline,
      hasDeadline:
        deadlineFilter?.type === FilterDocumentDateFilterTypes.HasDeadline,
      deadlineDeadlineStartDate,
      deadlineDeadlineEndDate,
      deadlineStartDate,
      deadlineEndDate,
      completedStartDate:
        completedStartDate ?? deadlineDeadlineCompletedStartDate,
      completedEndDate: completedEndDate ?? deadlineDeadlineCompletedEndDate,
      noLifeArea: noLifeArea ? true : undefined,
      lifeAreaLinkedGoalIds,
      noGoal: noGoal ? true : undefined,
      showCompleted:
        deleted ||
        (filterDocument.showCompleted ??
          (uncompletedOnly === undefined ? false : !uncompletedOnly)),
      showCompletedForDate: filterDocument.showCompletedForDate ?? undefined,
      includeTaskWithAction,
      excludeHiddenTodoHabits,
      archivedGoalIds,
      includeArchived,
      deleted,
      sortingMode: filterDocument.sortingMode ?? undefined,
    };
  };

  // A dimension that holds both specific ids and its "other"/none sentinel
  // (e.g. life area "Work" + "No life area") is an OR *within* that dimension.
  // Expand it into atomic filter sets so the downstream `.some()` ORs them,
  // while filters across dimensions stay AND'd. Splits only when a valid
  // specific id and the sentinel coexist — every other case stays a single,
  // unchanged set.
  const splitSelectedAndNoneFilters = (
    fdFilters: Partial<FilterDocumentFilters>,
  ): Partial<FilterDocumentFilters>[] => {
    const lifeAreaInclude = fdFilters.lifeArea?.include ?? [];
    const lifeAreaOptions =
      lifeAreaInclude.includes(lifeAreaOtherID) &&
      lifeAreaInclude.some(
        (id) => id !== lifeAreaOtherID && lifeAreaIdsSet.has(id),
      )
        ? [
            {
              ...fdFilters.lifeArea,
              include: lifeAreaInclude.filter((id) => id !== lifeAreaOtherID),
            },
            { ...fdFilters.lifeArea, include: [lifeAreaOtherID] },
          ]
        : [fdFilters.lifeArea];

    const goalInclude = fdFilters.goal?.include ?? [];
    const goalOptions =
      goalInclude.includes(goalOtherID) &&
      goalInclude.some((id) => id !== goalOtherID && goalIdsSet.has(id))
        ? [
            {
              ...fdFilters.goal,
              include: goalInclude.filter((id) => id !== goalOtherID),
            },
            { ...fdFilters.goal, include: [goalOtherID] },
          ]
        : [fdFilters.goal];

    return lifeAreaOptions.flatMap((lifeArea) =>
      goalOptions.map((goal) => ({ ...fdFilters, lifeArea, goal })),
    );
  };

  const baseOptionsList = splitSelectedAndNoneFilters(
    filterDocument.filters ?? {},
  ).map((combo) => calculateOptions(combo));

  if (filterDocument.or?.length) {
    return filterDocument.or.flatMap((orFilters) =>
      splitSelectedAndNoneFilters(orFilters).flatMap((orCombo) => {
        const orOptions = calculateOptions(orCombo);
        return baseOptionsList.map((baseOptions) =>
          mergeWith(
            {},
            baseOptions,
            orOptions,
            (objValue: unknown, srcValue: unknown) =>
              srcValue !== undefined ? srcValue : objValue,
          ),
        );
      }),
    );
  }

  return baseOptionsList;
};
